import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { blogApi } from "../lib/blogApi";
import { Snowflake, ArrowLeft, Clock, Calendar, User, Share2, Tag } from "lucide-react";
import { toast } from "sonner";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    blogApi
      .get(slug)
      .then((res) => setPost(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    blogApi.list().then((res) => {
      setRelated((res.data || []).filter((p) => p.slug !== slug).slice(0, 3));
    });
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: post.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
    } catch {
      /* user dismissed share */
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  }
  if (notFound || !post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-2xl font-bold text-slate-900">Post not found</h1>
        <p className="text-slate-500">The article you're looking for doesn't exist or has been removed.</p>
        <button
          onClick={() => navigate("/blog")}
          data-testid="blog-back-to-list"
          className="px-5 py-2.5 rounded-lg bg-sky-500 text-white font-semibold hover:bg-sky-600 transition-colors"
        >
          Back to Blog
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky compact header */}
      <header className="bg-white/90 border-b border-slate-100 sticky top-0 z-30 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-400 to-cyan-300 flex items-center justify-center">
              <Snowflake className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-slate-900">Frostyware 3D</span>
          </Link>
          <Link
            to="/blog"
            data-testid="back-to-blog"
            className="text-sm text-slate-500 hover:text-sky-600 flex items-center gap-1.5 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            All Posts
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 pt-12 pb-20">
        {post.category && (
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sky-50 text-sky-700 text-xs font-semibold uppercase tracking-wider hover:bg-sky-100 transition-colors"
          >
            <Tag className="w-3 h-3" />
            {post.category}
          </Link>
        )}

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight tracking-tight mt-6">
          {post.title}
        </h1>
        {post.subtitle && (
          <p className="text-lg text-slate-500 mt-4 leading-relaxed">{post.subtitle}</p>
        )}

        <div className="flex flex-wrap items-center gap-5 text-sm text-slate-500 mt-8 pb-8 border-b border-slate-100">
          <span className="flex items-center gap-1.5">
            <User className="w-4 h-4" />
            {post.author}
          </span>
          <span className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4" />
            {formatDate(post.created_at)}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            {post.read_minutes} min read
          </span>
          <button
            onClick={handleShare}
            data-testid="share-post-btn"
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 hover:border-sky-300 hover:bg-sky-50 transition-colors text-slate-600"
          >
            <Share2 className="w-3.5 h-3.5" />
            Share
          </button>
        </div>

        {post.cover_image && (
          <div className="my-8 rounded-2xl overflow-hidden bg-slate-100 aspect-[16/9]">
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="prose prose-slate prose-lg max-w-none
                        prose-headings:font-bold prose-headings:text-slate-900 prose-headings:tracking-tight
                        prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
                        prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                        prose-p:text-slate-700 prose-p:leading-relaxed
                        prose-a:text-sky-600 prose-a:no-underline hover:prose-a:underline
                        prose-strong:text-slate-900
                        prose-ul:my-4 prose-li:my-1
                        prose-blockquote:border-l-sky-400 prose-blockquote:bg-sky-50/40 prose-blockquote:px-5 prose-blockquote:py-1 prose-blockquote:not-italic
                        prose-table:border-collapse prose-table:my-6
                        prose-th:bg-slate-100 prose-th:p-3 prose-th:text-left prose-th:font-semibold
                        prose-td:p-3 prose-td:border-t prose-td:border-slate-100">
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </div>

        {/* Author / CTA card */}
        <div className="mt-14 p-6 rounded-2xl bg-gradient-to-br from-sky-50 via-white to-cyan-50 border border-sky-100">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-400 to-cyan-300 flex items-center justify-center flex-shrink-0">
              <Snowflake className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Written by</p>
              <p className="text-lg font-semibold text-slate-900">{post.author}</p>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Founder of Frostyware 3D. Sharing what I've learned running a 3D printing workshop in Chandigarh.
              </p>
              <Link
                to="/#products"
                data-testid="post-cta-shop"
                className="inline-flex items-center gap-1.5 mt-4 text-sky-600 font-medium hover:gap-2.5 transition-all"
              >
                Browse the Frostyware catalog →
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Related posts */}
      {related.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 pb-20">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Keep reading</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link
                key={r.id}
                to={`/blog/${r.slug}`}
                data-testid={`related-${r.slug}`}
                className="group block bg-white border border-slate-100 rounded-2xl overflow-hidden hover:border-sky-200 hover:shadow-lg transition-all"
              >
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  {r.cover_image && (
                    <img
                      src={r.cover_image}
                      alt={r.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                </div>
                <div className="p-5">
                  {r.category && <p className="text-xs font-semibold text-sky-600 uppercase tracking-wider mb-1.5">{r.category}</p>}
                  <h3 className="text-base font-bold text-slate-900 leading-snug line-clamp-2 group-hover:text-sky-600 transition-colors">
                    {r.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <footer className="border-t border-slate-100 bg-slate-50 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-400">
          <span>© {new Date().getFullYear()} Frostyware 3D. All rights reserved.</span>
          <div className="flex gap-5">
            <Link to="/privacy" className="hover:text-sky-500 transition-colors">Privacy Policy</Link>
            <Link to="/" className="hover:text-sky-500 transition-colors">Home</Link>
            <Link to="/blog" className="hover:text-sky-500 transition-colors">Blog</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
